import { useState, useMemo } from 'react';
import { Node } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { breakpoints, useTargetElementContainerQuery } from '@frontend/responsiveness';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import { Tray } from '@frontend/design-system';
import { NumbersPanel } from '../numbers-panel/numbers-panel';
import { OverridePanel } from '../override-panel';
import type { ExtendedCallRoute } from '../types';
import { PhoneHoursNodeState } from './add-step-panel/types';
import { SidePanel } from './constants';
import type { CallRouteModalControls } from './hooks/use-call-route-modals';
import { LocationPanel } from './location-panel';
import { PhoneHoursPanel } from './phone-hours-panel';

export interface CallRouteSidePanelTrayProps {
  // Panel state
  activePanel: string | undefined;
  onPanelClose: () => void;

  // Modal controls
  sidePanelModal: CallRouteModalControls['sidePanel'];
  editPhoneNumbersModal: CallRouteModalControls['editPhoneNumbers'];
  editExtensionsModal: CallRouteModalControls['editExtensions'];
  editOverrideModal: CallRouteModalControls['editOverride'];
  changeLocationModal: CallRouteModalControls['changeLocation'];

  // Call route data
  extendedCallRoute: ExtendedCallRoute | undefined;
  isDeviceExtensionRoute: boolean;
  isSingleTypeScope: boolean;

  // Location data
  locationName: string;
  locationId: string;

  // Phone hours
  officeHourNodes: Node[];
  onSavePhoneHours: (params: { nodeId: string; nodeUpdate: PhoneHoursNodeState }) => void;
  hoursManagementPermission: boolean;
  triggerPhoneHoursEdit?: boolean; // New prop to trigger edit mode
  isCalendarViewOpen?: boolean;
  onCalendarViewChange?: (isOpen: boolean) => void;
  processingNodeId?: string; // Node ID being processed from canvas edit action
}

/**
 * Side panel tray for call route details.
 * Displays different panels based on the activePanel prop (e.g. Numbers, Override, Location and PhoneHours panels).
 */
export const CallRouteSidePanelTray = ({
  activePanel,
  onPanelClose,
  sidePanelModal,
  editPhoneNumbersModal,
  editExtensionsModal,
  editOverrideModal,
  changeLocationModal,
  extendedCallRoute,
  isDeviceExtensionRoute,
  isSingleTypeScope,
  locationName,
  locationId,
  officeHourNodes,
  onSavePhoneHours,
  hoursManagementPermission,
  triggerPhoneHoursEdit,
  isCalendarViewOpen = false,
  onCalendarViewChange,
  processingNodeId,
}: CallRouteSidePanelTrayProps) => {
  const [phoneHoursIsEditMode, setPhoneHoursIsEditMode] = useState(false);
  const mainContentElement = useMemo(() => document.querySelector('#main-content') as HTMLElement, []);
  const isPhoneHoursPanel = activePanel === SidePanel.PhoneHours;
  const [isSmallMode] = useTargetElementContainerQuery({
    maxWidth: isPhoneHoursPanel && phoneHoursIsEditMode ? breakpoints.large.min : breakpoints.small.max,
    element: mainContentElement,
  });

  if (!extendedCallRoute) {
    return null;
  }

  const handleEditPhoneNumbers = () => {
    onPanelClose();
    editPhoneNumbersModal.openModal();
  };

  const handleEditExtensions = () => {
    onPanelClose();
    editExtensionsModal.openModal();
  };

  const handleChangeLocation = () => {
    onPanelClose();
    changeLocationModal.openModal();
  };

  return (
    <Tray
      css={{
        borderLeft: `solid ${theme.colors.neutral10}`,
        overflowY: 'scroll',
        maxWidth:
          isPhoneHoursPanel &&
          isCalendarViewOpen &&
          !isSmallMode &&
          !(officeHourNodes.length > 1 && !phoneHoursIsEditMode)
            ? '100%'
            : undefined,
        transition: ' max-width 800ms ease',
      }}
      mountTarget='#call-route-flow-section'
      width={phoneHoursIsEditMode && !isSmallMode ? 'xlarge' : 'large'}
      {...sidePanelModal.modalProps}
      showBackdrop={false}
    >
      <SurfaceHeader.Root noBorder css={{ margin: theme.spacing(-3, -3, -2, -3), width: 'calc(100% + 48px)' }}>
        <SurfaceHeader.Row>
          <SurfaceHeader.Title>{activePanel ?? ''}</SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            <SurfaceHeader.Icon iconName='x' onClick={onPanelClose} />
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>

      {activePanel === SidePanel.Numbers && (
        <NumbersPanel
          callRoute={extendedCallRoute}
          hasChildLocations={!isSingleTypeScope}
          isDeviceExtensionRoute={isDeviceExtensionRoute}
          onEditPhoneNumbers={handleEditPhoneNumbers}
          onEditExtensions={handleEditExtensions}
        />
      )}

      {activePanel === SidePanel.Override && (
        <OverridePanel callRoute={extendedCallRoute} locationId={locationId} editOverrideTray={editOverrideModal} />
      )}

      {activePanel === SidePanel.Location && (
        <LocationPanel locationName={locationName} onChangeLocationClick={handleChangeLocation} />
      )}

      {activePanel === SidePanel.PhoneHours && (
        <PhoneHoursPanel
          officeHourNodes={officeHourNodes}
          onSavePhoneHours={onSavePhoneHours}
          hoursManagementPermission={hoursManagementPermission}
          onCalendarViewChange={onCalendarViewChange}
          onEditModeChange={setPhoneHoursIsEditMode}
          triggerPhoneHoursEdit={triggerPhoneHoursEdit}
          isCalendarViewOpen={isCalendarViewOpen}
          processingNodeId={processingNodeId}
          isSmallMode={isSmallMode}
        />
      )}
    </Tray>
  );
};
